/**
 * passkey-budget WP1: lock follows the OS (docs/security/passkey-budget.md P1).
 *
 * On a computer whose vikod this browser enrolled (it holds the enrollment pin) and that the user
 * bound as "this computer" with a passkey (or by adding it locally), the app lock clears without a
 * passkey once vikod signs that the OS screen locked at or after `since` and then unlocked. The
 * proof is bound to a fresh nonce and this browser's device key, verified with the local pin
 * only, and clears only the app lock (never content keys, never a risky command).
 */
import { newPresenceNonce, verifyPresenceUnlock } from '@viko/crypto'
import { loadContentDaemonPin } from './content-enrollment.js'
import { idb } from './idb.js'

const KEY = 'app-lock/presence-unlock/v1'
export interface PresenceBinding {
  accountId: string
  machineId: string
  enabledAt: number
}

export const loadPresenceBinding = () =>
  idb.get<PresenceBinding>(KEY).catch(() => undefined) as Promise<PresenceBinding | undefined>
export const clearPresenceBinding = () => idb.del(KEY)

/**
 * Bind this browser to `machineId` for presence unlock. Needs this browser's enrollment pin for
 * the machine and a user-verified passkey ceremony (`verifyPasskey`), unless the caller is the
 * local enrollment itself, whose approval was that ceremony (`approvedByEnrollment`).
 */
export async function enablePresenceUnlock(o: {
  accountId: string
  machineId: string
  verifyPasskey?: () => Promise<void>
  approvedByEnrollment?: boolean
  now?: () => number
}): Promise<PresenceBinding> {
  if (!(await loadContentDaemonPin(o.accountId, o.machineId)))
    throw new Error('Approve this machine from this browser first (its key must be pinned here).')
  if (!o.approvedByEnrollment) {
    if (!o.verifyPasskey) throw new Error('Turning this on needs your passkey.')
    await o.verifyPasskey()
  }
  const binding = {
    accountId: o.accountId,
    machineId: o.machineId,
    enabledAt: (o.now ?? Date.now)(),
  }
  await idb.set(KEY, binding)
  return binding
}

export interface PresenceUnlockDeps {
  accountId: () => Promise<string | undefined>
  deviceKeyId: () => Promise<string | undefined>
  /** Send `presence.unlock_proof` to the machine; resolves with the daemon's result. */
  request: (machineId: string, payload: { nonce: string; since: number }) => Promise<unknown>
  sessionOnly?: () => boolean
  now?: () => number
}

/** Ask the bound machine for a proof and verify it locally. Never throws; false = use the passkey. */
export async function tryPresenceUnlock(d: PresenceUnlockDeps, since: number): Promise<boolean> {
  try {
    if (d.sessionOnly?.()) return false
    const binding = await loadPresenceBinding()
    const accountId = await d.accountId()
    const deviceKeyId = await d.deviceKeyId()
    if (!binding || !accountId || !deviceKeyId || binding.accountId !== accountId) return false
    const pin = await loadContentDaemonPin(accountId, binding.machineId)
    if (!pin) return false
    const nonce = newPresenceNonce()
    const proof = await d.request(binding.machineId, { nonce, since })
    const r = await verifyPresenceUnlock(proof, pin, {
      accountId,
      machineId: binding.machineId,
      deviceKeyId,
      nonce,
      since,
      now: (d.now ?? Date.now)(),
    })
    return r.ok
  } catch {
    return false
  }
}
